import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSupabase } from '../supabaseAuth';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState(false);
  const { resetPassword, loading, error } = useSupabase();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!email) {
      setFormError('Please enter your email address');
      return;
    }

    try {
      await resetPassword(email);
      setSuccess(true);
    } catch (error) {
      console.error('Reset password error:', error);
      setFormError(error.message || 'Failed to send reset email. Please try again.');
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 pt-20 md:pt-24">
      <main className="w-full flex flex-col gap-2.5 items-center p-5 md:p-12 md:pt-0">
        {/* Page Header */}
        <h1 className="text-3xl font-bold text-black mb-4">Forgot Password</h1>

        {/* Container */}
      <div className="bg-white rounded-2xl shadow-md w-full max-w-2xl flex flex-col items-center gap-3 p-8 md:p-10">
        <div className="flex flex-col w-full max-w-md gap-4 flex-1">

          {/* Header Section */}
          <div className="flex flex-col items-center text-center w-full mb-2">
            <h2 className="text-[26px] font-semibold text-black mb-1">Reset Your Password</h2>
            <p className="text-gray-600 text-base leading-relaxed">
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {/* Success Message */}
          {success ? (
            <div className="flex flex-col gap-4">
              <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                Password reset email sent! Check your inbox for a link to reset your password.
              </div>
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-black no-underline font-bold hover:underline"
                >
                  Back to Login
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* Error Messages */}
              {(error || formError) && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {formError || error}
                </div>
              )}

              {/* Form Section */}
              <form onSubmit={handleSubmit} className="flex flex-col justify-between flex-1">
                <div className="flex flex-col gap-2.5">
                  {/* Email Field */}
                  <div className="flex flex-col">
                    <label className="font-medium mb-1">Email Address</label>
                    <input
                      type="email"
                      className="border border-gray-300 rounded-lg p-3 text-[0.95rem] transition-all focus:outline-none focus:border-black focus:ring-2 focus:ring-gray-100"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                      required
                      autoFocus
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex flex-col items-center w-full gap-3 mt-4">
                  <button
                    type="submit"
                    className="w-full h-12 rounded-full bg-black text-white font-bold text-base border-none hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>

                  {/* Back to Login Link */}
                  <div className="text-center text-gray-700">
                    Remember your password?{' '}
                    <Link
                      to="/login"
                      className="text-black no-underline font-bold hover:underline"
                    >
                      Log In
                    </Link>
                  </div>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
      </main>
    </div>
  );
};

export default ForgotPassword;
